import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { ApiError } from '@formosa/shared/api/createClient';
import {
  CONFIG_ODDS_METHOD_OPTIONS,
  CONFIG_OVERRIDE_OPTIONS,
} from './futuresRoundConfigOptions';

const DEFAULT_FORM = {
  oddsMethod: 'RANDOM_RATE',
  overrideMethod: 'GLOBAL_CONTROL',
};

export default function FuturesRoundBatchConfigModal({
  open,
  roundCodes,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(DEFAULT_FORM);
      setError('');
    }
  }, [open, roundCodes]);

  if (!open) {
    return null;
  }

  function handleReset() {
    setForm(DEFAULT_FORM);
    setError('');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!roundCodes?.length) {
      setError('請先勾選可配置的週期');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await adminApi.configFuturesRounds({
        roundCodes,
        oddsMethod: form.oddsMethod,
        overrideMethod: form.overrideMethod,
      });
      const entries = Object.values(result || {});
      const ok = entries.filter((item) => item?.success).length;
      const failed = entries.filter((item) => !item?.success);
      if (failed.length > 0) {
        const firstMsg = failed[0]?.message || '部分週期配置失敗';
        setError(`成功 ${ok}/${roundCodes.length}：${firstMsg}`);
      }
      onSaved?.({ ok, total: roundCodes.length, failed: failed.length });
      if (failed.length === 0) {
        onClose?.();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '批量配置失敗');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>批量配置</h3>
          <button type="button" className="admin-modal__close" onClick={onClose} aria-label="關閉">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="admin-modal__body admin-modal__body--form">
            {error && <div className="alert alert-error">{error}</div>}

            <fieldset className="config-fieldset">
              <legend>賠率設置 *</legend>
              {CONFIG_ODDS_METHOD_OPTIONS.map((item) => (
                <label key={item.value} className="radio-inline">
                  <input
                    type="radio"
                    name="batchOddsMethod"
                    value={item.value}
                    checked={form.oddsMethod === item.value}
                    onChange={() => setForm((prev) => ({ ...prev, oddsMethod: item.value }))}
                  />
                  {item.label}
                </label>
              ))}
            </fieldset>

            <fieldset className="config-fieldset">
              <legend>場控設置 *</legend>
              {CONFIG_OVERRIDE_OPTIONS.map((item) => (
                <label key={item.value} className="radio-inline">
                  <input
                    type="radio"
                    name="batchOverrideMethod"
                    value={item.value}
                    checked={form.overrideMethod === item.value}
                    onChange={() =>
                      setForm((prev) => ({ ...prev, overrideMethod: item.value }))
                    }
                  />
                  {item.label}
                </label>
              ))}
            </fieldset>

            <div className="form-field">
              <label htmlFor="batchRoundCodes">週期 ID</label>
              <textarea
                id="batchRoundCodes"
                className="config-round-codes"
                rows={4}
                readOnly
                value={roundCodes.join(', ')}
              />
            </div>
          </div>

          <div className="admin-modal__footer admin-modal__footer--center">
            <button type="submit" className="btn btn-success" disabled={submitting || !roundCodes.length}>
              {submitting ? '提交中…' : '提交'}
            </button>
            <button type="button" className="btn btn-warning" onClick={handleReset} disabled={submitting}>
              重置
            </button>
            <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
              關閉
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
